import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MergeFieldInserter } from '@/components/messaging/merge-field-inserter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import { SwitchField } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { insertAtSelection } from '@/lib/messaging-merge-fields';
import type { ReceiptSmsFundOverride, ReceiptSmsSettings } from '@/lib/giving-default-funds';

import { ReceiptSmsOverrideDialog } from './receipt-sms-override-dialog';

interface GivingReceiptSmsFieldsProps {
  value: ReceiptSmsSettings;
  onChange: (next: ReceiptSmsSettings) => void;
  disabled?: boolean;
  popoverPortal?: 'dialog' | 'body';
}

interface OverrideTableRow extends ReceiptSmsFundOverride {
  index: number;
}

interface FundOption {
  id: string;
  name: string;
}

const PREVIEW_MAX = 80;

export const GivingReceiptSmsFields = ({
  value,
  onChange,
  disabled = false,
  popoverPortal = 'dialog',
}: GivingReceiptSmsFieldsProps) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'active'],
    queryFn: () =>
      api.get<{ data: FundOption[] }>('/giving/funds?pageSize=100&activeOnly=true'),
  });

  const fundNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const fund of funds.data?.data ?? []) {
      map.set(fund.id, fund.name);
    }
    return map;
  }, [funds.data?.data]);

  const rememberSelection = () => {
    const el = bodyRef.current;
    if (!el) return;
    setSelection({
      start: el.selectionStart ?? value.body.length,
      end: el.selectionEnd ?? value.body.length,
    });
  };

  const insertIntoDefault = (token: string) => {
    const { value: next, caret } = insertAtSelection(
      value.body,
      selection.start,
      selection.end,
      token,
    );
    onChange({ ...value, body: next });
    setSelection({ start: caret, end: caret });
    requestAnimationFrame(() => {
      bodyRef.current?.focus();
      bodyRef.current?.setSelectionRange(caret, caret);
    });
  };

  const tableRows: OverrideTableRow[] = value.fundOverrides.map((row, index) => ({
    ...row,
    index,
  }));

  const editingOverride =
    editingIndex != null ? (value.fundOverrides[editingIndex] ?? null) : null;

  const openAddOverride = () => {
    setEditingIndex(null);
    setOverrideDialogOpen(true);
  };

  const openEditOverride = (index: number) => {
    setEditingIndex(index);
    setOverrideDialogOpen(true);
  };

  const saveOverride = (override: ReceiptSmsFundOverride) => {
    if (editingIndex != null) {
      onChange({
        ...value,
        fundOverrides: value.fundOverrides.map((row, rowIndex) =>
          rowIndex === editingIndex ? override : row,
        ),
      });
      return;
    }
    onChange({
      ...value,
      fundOverrides: [...value.fundOverrides, override],
    });
  };

  const removeOverride = (index: number) => {
    onChange({
      ...value,
      fundOverrides: value.fundOverrides.filter((_, rowIndex) => rowIndex !== index),
    });
  };

  const columns: DataTableColumn<OverrideTableRow>[] = [
    {
      id: 'fund',
      header: t('funds.settings.receiptSms.overrideColumns.fund'),
      cell: (row) => fundNameById.get(row.fundId) ?? '—',
    },
    {
      id: 'message',
      header: t('funds.settings.receiptSms.overrideColumns.message'),
      cell: (row) => {
        const text = row.body.trim();
        if (!text) return '—';
        return text.length > PREVIEW_MAX ? `${text.slice(0, PREVIEW_MAX)}…` : text;
      },
      hideBelowMd: true,
    },
  ];

  return (
    <div className="space-y-4">
      <SwitchField
        id="giving-receipt-sms-enabled"
        label={t('funds.settings.receiptSms.enabled')}
        description={t('funds.settings.receiptSms.enabledDescription')}
        checked={value.enabled}
        disabled={disabled}
        onCheckedChange={(checked) => onChange({ ...value, enabled: checked })}
      />

      <FormField
        label={t('funds.settings.receiptSms.defaultMessage')}
        hint={t('funds.settings.receiptSms.defaultMessageHelp')}
      >
        {({ id }) => (
          <>
            <Textarea
              ref={bodyRef}
              id={id}
              value={value.body}
              disabled={disabled || !value.enabled}
              onChange={(event) => onChange({ ...value, body: event.target.value })}
              onSelect={rememberSelection}
              onKeyUp={rememberSelection}
              onClick={rememberSelection}
              onBlur={rememberSelection}
              rows={4}
            />
            <MergeFieldInserter
              onInsert={insertIntoDefault}
              includeGivingFields
              disabled={disabled || !value.enabled}
            />
          </>
        )}
      </FormField>

      <div className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-body-sm font-medium text-fg">{t('funds.settings.receiptSms.overridesTitle')}</p>
            <p className="mt-1 text-caption text-fg-secondary">
              {t('funds.settings.receiptSms.overridesHelp')}
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0"
            leadingIcon={<Plus className="size-3.5" aria-hidden />}
            disabled={disabled || !value.enabled}
            onClick={openAddOverride}
          >
            {t('funds.settings.receiptSms.addOverride')}
          </Button>
        </div>

        <DataTable
          rows={tableRows}
          columns={columns}
          rowKey={(row) => `${row.fundId}-${row.index}`}
          loading={funds.isLoading}
          empty={
            <p className="text-body-sm text-fg-secondary">
              {t('funds.settings.receiptSms.overridesEmpty')}
            </p>
          }
          actionsHeader={tCommon('table.actions')}
          rowActions={(row) => (
            <Button
              size="sm"
              variant="outline"
              disabled={disabled || !value.enabled}
              onClick={() => openEditOverride(row.index)}
            >
              {tCommon('action.edit')}
            </Button>
          )}
        />
      </div>

      <ReceiptSmsOverrideDialog
        open={overrideDialogOpen}
        onOpenChange={(next) => {
          setOverrideDialogOpen(next);
          if (!next) setEditingIndex(null);
        }}
        override={editingOverride}
        onSave={saveOverride}
        onDelete={editingIndex != null ? () => removeOverride(editingIndex) : undefined}
        disabled={disabled || !value.enabled}
        popoverPortal={popoverPortal}
      />
    </div>
  );
};
